<script setup>
// =============================================================================
// FangateCollaborations.vue — Vault "Collaborations" module
// =============================================================================
// Sections (action-driven ordering):
//   1. Incoming requests       — someone wants to tag YOU on their product.
//                                Accept / Decline actions live here.
//   2. Outgoing pending        — YOU've tagged someone, they haven't
//                                responded yet. View-only status.
//   3. Tagged content          — content you ALREADY accepted being tagged on
//                                (passive list — your "credits" reel).
//   4. Your creator network    — creators that have accepted you in the past,
//                                so you can re-tag them without a new approval
//                                request.
//
// Data sourcing:
//   • Section 1 + 3 → ONE call to GET /consent-tags, split by status
//     (pending vs. accepted).
//   • Section 2 + 4 → aggregate across all products via GET
//     /products/{id}/consent-creator-tags. Parallel-fetched in batches; we
//     accept the cost for now (typical creators have <100 products).
//
// API key 403 behaviour:
//   The consent-tag endpoint group was previously API-key-blocked. Fangate
//   re-opened them per support email (May 2026) — but if they ever close
//   them again, EVERY section here will 403. We surface a single
//   "session-only" info card instead of red errors so the user understands
//   it's a Fangate scope issue, not a Creator-Chat bug.
// =============================================================================

import { ref, reactive, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import FangateAPI from 'dashboard/api/fangate';

// Module-level cache so re-mounts (account switches) don't re-fetch the
// same products. Reactive so resolveProduct() updates the rendered rows
// as soon as enrichment lands.
const productCache = reactive({});

const props = defineProps({
  // Just for friendly copy in the empty-state CTA — falls back to "Creator".
  accountName: { type: String, default: '' },
});

// ---- State -----------------------------------------------------------------
const incoming         = ref([]);  // pending → action needed
const taggedContent    = ref([]);  // accepted on YOUR side
const outgoingPending  = ref([]);  // pending FROM you
const creatorNetwork   = ref([]);  // unique creators with accepted outgoing

const isLoading        = ref(false);
const respondingId     = ref(null);  // id of the tag currently mid-accept/decline
const error            = ref('');
// `apiKeyBlocked` toggles a single info card instead of the section errors
// when Fangate returns "This API key cannot access this endpoint." across
// the board — it means the whole feature group is session-only, not a bug
// on our side.
const apiKeyBlocked    = ref(false);

// ---- Data fetch ------------------------------------------------------------
const looksApiKeyBlocked = msg =>
  /api key cannot access this endpoint/i.test(String(msg || ''));

async function loadIncomingAndTagged() {
  // Single fetch covers both sections — we just split by status.
  const all = await FangateAPI.listIncomingConsentTags();
  incoming.value      = (all || []).filter(t => t.status === 'pending');
  taggedContent.value = (all || []).filter(t => t.status === 'accepted');

  // Enrich every unique product_id with title + URL so the rows can render
  // a clickable product link instead of just "Product #5155". Runs in the
  // background — rows stay rendered with the ID-only fallback until the
  // enrichment promise resolves.
  enrichProductsForTags(all || []);
}

// Parallel-fetch GET /products/{id} for every unique product_id we see in
// a batch of consent-tags. Cached in module scope so a second account
// (and a second mount of this component) reuses anything we already have.
// Fangate returns 403 for products the API key can't see (e.g. products on
// another creator's account) — that's normal for incoming-tag products, we
// just keep the ID-only fallback when enrichment fails.
async function enrichProductsForTags(tags) {
  const ids = Array.from(new Set(
    (tags || []).map(t => t?.product_id).filter(id => id && !productCache[id])
  ));
  if (!ids.length) return;
  await Promise.all(ids.map(async id => {
    try {
      const p = await FangateAPI.getProduct(id);
      // Different Fangate endpoints unwrap differently; accept either shape.
      productCache[id] = p?.data || p;
    } catch (_) {
      // Stamp the cache with the bare id so we don't re-attempt on every
      // re-render. The "Product #<id>" fallback in the UI still works.
      productCache[id] = { id, _enrichmentFailed: true };
    }
  }));
}

async function loadOutgoingAndNetwork() {
  // Pull product list (page 1 only for now — pagination later if needed).
  const productResp = await FangateAPI.listProducts({ page: 1, limit: 100 });
  const products    = Array.isArray(productResp) ? productResp
                    : (productResp?.data ?? productResp?.products ?? []);
  if (!products.length) {
    outgoingPending.value = [];
    creatorNetwork.value  = [];
    return;
  }

  // Parallel-fetch consent tags per product. A failed per-product call
  // shouldn't kill the whole load — fall back to an empty array.
  const tagBatches = await Promise.all(
    products.map(p =>
      FangateAPI.listProductConsentTags(p.id).catch(() => [])
    )
  );

  // Stitch the parent product back onto every tag for rendering.
  const allOutgoing = tagBatches.flatMap((batch, i) =>
    (batch || []).map(t => ({ ...t, product: products[i] }))
  );
  outgoingPending.value = allOutgoing.filter(t => t.status === 'pending');

  // Build the "creator network" — unique tagged_user per accepted tag.
  const accepted   = allOutgoing.filter(t => t.status === 'accepted');
  const byCreator  = new Map();
  for (const tag of accepted) {
    const u = tag.tagged_user;
    if (!u?.id) continue;
    if (!byCreator.has(u.id)) {
      byCreator.set(u.id, {
        ...u,
        // Counters / metadata that drive the "X products together" subline.
        productCount: 0,
        firstAcceptedAt: tag.taken_at || tag.created_at,
        lastAcceptedAt:  tag.taken_at || tag.created_at,
      });
    }
    const entry = byCreator.get(u.id);
    entry.productCount += 1;
    const stamp = tag.taken_at || tag.created_at;
    if (stamp && stamp > entry.lastAcceptedAt) entry.lastAcceptedAt = stamp;
  }
  creatorNetwork.value = Array.from(byCreator.values())
    .sort((a, b) => String(b.lastAcceptedAt || '').localeCompare(String(a.lastAcceptedAt || '')));
}

async function load() {
  isLoading.value    = true;
  error.value        = '';
  apiKeyBlocked.value = false;
  try {
    await Promise.all([loadIncomingAndTagged(), loadOutgoingAndNetwork()]);
  } catch (e) {
    const msg = e?.message || 'Failed to load collaborations.';
    if (looksApiKeyBlocked(msg)) {
      apiKeyBlocked.value = true;
    } else {
      error.value = msg;
    }
  } finally {
    isLoading.value = false;
  }
}

onMounted(load);

// ---- Actions ---------------------------------------------------------------
async function respond(tag, decision) {
  if (respondingId.value === tag.id) return;
  respondingId.value = tag.id;
  try {
    await FangateAPI.respondToConsentTag(tag.id, decision);
    // Optimistic local update — remove from incoming, push to taggedContent
    // if accepted. Decline simply drops from the list.
    incoming.value = incoming.value.filter(t => t.id !== tag.id);
    if (decision === 'accept') {
      taggedContent.value = [{ ...tag, status: 'accepted' }, ...taggedContent.value];
    }
    useAlert(decision === 'accept'
      ? 'Tag accepted — this content now lives in your tagged list.'
      : 'Tag declined.');
  } catch (e) {
    useAlert(e?.message || 'Failed to update the tag.');
  } finally {
    respondingId.value = null;
  }
}

// ---- Helpers ---------------------------------------------------------------
const fmtDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};
const fmtDateTime = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// Field-name choice per Fangate API docs (docs.fangate.app/api-reference/consents):
//   • GET /consent-tags          → `tagger`     { id, first_name, last_name, email }
//   • /products/{id}/consent-... → `tagged_user`{ +display_name }
// We keep a small fallback chain in case Fangate adds aliases later.
const resolveTagger = tag =>
  tag?.tagger || tag?.tagged_user || tag?.seller_user
   || tag?.user || tag?.creator || {};

// IMPORTANT: Fangate's consent-tag responses carry ONLY `product_id`, no
// embedded product object. resolveProduct returns a stub when the tag was
// loaded inline; enrichProductsForTags() then fills `title` / `url` from
// productCache after a parallel GET /products/{id} sweep. UI always falls
// back to "Product #<id>" so the ID stays visible even before enrichment.
const resolveProduct = tag => {
  const pid = tag?.product_id ?? tag?.product?.id;
  return tag?.product || productCache[pid] || { id: pid };
};

const userName = u => {
  if (!u) return 'Unknown creator';
  const full = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return u.display_name || full || u.name || u.email || 'Unknown creator';
};
const userEmail = u => u?.email || '';
const userInitial = u => (userName(u)?.charAt(0) || '?').toUpperCase();

// Product label always carries the ID so the agent has something to
// reference even before/without enrichment. When a real title is available
// we show "Untitled content #5155" patterns become "My Vault Pack #5155".
// "Untitled content" is the Fangate-native empty-title fallback.
const productTitle = p => {
  const t = p?.title || p?.name;
  return t || 'Untitled content';
};
const productIdLabel = p => p?.id ? `#${p.id}` : '';
// Prefer a content/preview URL (links to the upload itself, which is what
// the tagged creator cares about) before falling back to the public
// checkout URL.
const productUrl = p =>
  p?.content_url || p?.preview_url || p?.url || p?.checkout_url || '';

// Counts used in section eyebrow chips so the agent sees workload at a
// glance ("3 incoming · 5 pending").
const counts = computed(() => ({
  incoming:         incoming.value.length,
  outgoingPending:  outgoingPending.value.length,
  taggedContent:    taggedContent.value.length,
  creatorNetwork:   creatorNetwork.value.length,
}));
</script>

<template>
  <div class="fc-root">

    <!-- ===== Header ===== -->
    <header class="fc-head">
      <h2 class="fc-h1">Collaborations</h2>
      <p class="fc-sub">
        Manage co-creator tag requests — incoming, outgoing, and your
        existing creator network.
      </p>
      <button class="fc-refresh-btn" :disabled="isLoading"
              :title="isLoading ? 'Refreshing…' : 'Refresh from Fangate'"
              @click="load">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.2"
             stroke-linecap="round" stroke-linejoin="round"
             :class="{ 'fc-spin': isLoading }">
          <polyline points="23 4 23 10 17 10"/>
          <polyline points="1 20 1 14 7 14"/>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
        {{ isLoading ? 'Loading…' : 'Refresh' }}
      </button>
    </header>

    <!-- ===== Fangate API-key-scope notice (covers all sections) ===== -->
    <div v-if="apiKeyBlocked" class="fc-warn-card">
      <strong>Co-creator features are session-only on Fangate.</strong>
      The endpoints for tag management currently reject Creator-Chat's API
      key. Until Fangate opens them up, manage tags directly at
      <a href="https://app.fangate.info" target="_blank" rel="noopener">
        app.fangate.info → Settings → Tags
      </a>.
    </div>

    <!-- ===== Global error (not 403-scope) ===== -->
    <div v-else-if="error" class="fc-error-card">
      {{ error }}
      <button class="fc-error-retry" @click="load">Retry</button>
    </div>

    <!-- ===== SECTION 1: Incoming requests — needs your action ===== -->
    <section v-if="!apiKeyBlocked" class="fc-section">
      <div class="fc-section-head">
        <span class="fc-eyebrow fc-eyebrow--action">Incoming tag requests</span>
        <span v-if="counts.incoming" class="fc-count fc-count--action">
          {{ counts.incoming }} pending
        </span>
      </div>
      <h3 class="fc-section-title">Creators who want to tag you</h3>
      <p class="fc-section-body">
        Review requests from other creators and accept or decline the tag.
      </p>

      <div class="fc-card">
        <div v-if="isLoading && !counts.incoming" class="fc-empty">
          Loading…
        </div>
        <template v-else-if="!counts.incoming">
          <div class="fc-empty-title">No incoming requests</div>
          <div class="fc-empty-body">
            When another creator tags you, the request will appear here with
            Accept and Decline actions.
          </div>
        </template>
        <ul v-else class="fc-list">
          <li v-for="tag in incoming" :key="tag.id" class="fc-row fc-row--stacked">
            <div class="fc-avatar">{{ userInitial(resolveTagger(tag)) }}</div>
            <div class="fc-row-main">
              <div class="fc-row-title-line">
                <a v-if="productUrl(resolveProduct(tag))"
                   :href="productUrl(resolveProduct(tag))"
                   target="_blank" rel="noopener"
                   class="fc-row-title fc-row-title--link"
                   :title="'Open product content in a new tab'">
                  {{ productTitle(resolveProduct(tag)) }}
                  <span class="fc-row-pid">{{ productIdLabel(resolveProduct(tag)) }}</span>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
                <span v-else class="fc-row-title">
                  {{ productTitle(resolveProduct(tag)) }}
                  <span class="fc-row-pid">{{ productIdLabel(resolveProduct(tag)) }}</span>
                </span>
                <span class="fc-status fc-status--pending">Pending</span>
              </div>
              <div class="fc-row-sub">
                Requested by <strong>{{ userName(resolveTagger(tag)) }}</strong>
                <span v-if="userEmail(resolveTagger(tag))" class="fc-row-meta">
                  · {{ userEmail(resolveTagger(tag)) }}
                </span>
              </div>
              <div class="fc-row-sub fc-row-sub--soft">
                Requested {{ fmtDateTime(tag.created_at) }}
              </div>
            </div>
            <div class="fc-row-actions">
              <button class="fc-btn fc-btn--ghost"
                      :disabled="respondingId === tag.id"
                      @click="respond(tag, 'decline')">
                Decline
              </button>
              <button class="fc-btn fc-btn--primary"
                      :disabled="respondingId === tag.id"
                      @click="respond(tag, 'accept')">
                {{ respondingId === tag.id ? '…' : 'Accept' }}
              </button>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <!-- ===== SECTION 2: Outgoing pending — waiting on others ===== -->
    <section v-if="!apiKeyBlocked" class="fc-section">
      <div class="fc-section-head">
        <span class="fc-eyebrow">Pending tag requests you sent</span>
        <span v-if="counts.outgoingPending" class="fc-count">
          {{ counts.outgoingPending }} waiting
        </span>
      </div>
      <h3 class="fc-section-title">Waiting on creator approval</h3>
      <p class="fc-section-body">
        These creators still need to accept your consent request before
        their tag becomes active.
      </p>

      <div class="fc-card">
        <div v-if="isLoading && !counts.outgoingPending" class="fc-empty">
          Loading…
        </div>
        <template v-else-if="!counts.outgoingPending">
          <div class="fc-empty-title">No pending outgoing requests</div>
          <div class="fc-empty-body">
            You do not have any outgoing tag requests waiting for approval.
          </div>
        </template>
        <ul v-else class="fc-list">
          <li v-for="tag in outgoingPending" :key="tag.id" class="fc-row">
            <div class="fc-avatar fc-avatar--soft">{{ userInitial(tag.tagged_user) }}</div>
            <div class="fc-row-main">
              <div class="fc-row-title">{{ userName(tag.tagged_user) }}</div>
              <div class="fc-row-sub">
                tagged on <strong>{{ productTitle(tag.product) }}</strong>
                <span v-if="userEmail(tag.tagged_user)" class="fc-row-meta">
                  · {{ userEmail(tag.tagged_user) }}
                </span>
                <span class="fc-row-meta">· sent {{ fmtDateTime(tag.created_at) }}</span>
              </div>
            </div>
            <span class="fc-status fc-status--pending">Pending</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- ===== SECTION 3: Tagged content — your accepted credits ===== -->
    <section v-if="!apiKeyBlocked" class="fc-section">
      <div class="fc-section-head">
        <span class="fc-eyebrow">Content you are tagged in</span>
        <span v-if="counts.taggedContent" class="fc-count">
          {{ counts.taggedContent }}
        </span>
      </div>
      <h3 class="fc-section-title">Accepted tagged content</h3>
      <p class="fc-section-body">
        Content items where you have already accepted the tag request.
      </p>

      <div class="fc-card">
        <div v-if="isLoading && !counts.taggedContent" class="fc-empty">
          Loading…
        </div>
        <template v-else-if="!counts.taggedContent">
          <div class="fc-empty-title">No accepted tagged content yet</div>
          <div class="fc-empty-body">
            When you accept a tag request, the linked content will appear here.
          </div>
        </template>
        <ul v-else class="fc-list">
          <li v-for="tag in taggedContent" :key="tag.id" class="fc-row">
            <div class="fc-avatar fc-avatar--soft">{{ userInitial(resolveTagger(tag)) }}</div>
            <div class="fc-row-main">
              <a v-if="productUrl(resolveProduct(tag))"
                 :href="productUrl(resolveProduct(tag))"
                 target="_blank" rel="noopener"
                 class="fc-row-title fc-row-title--link">
                {{ productTitle(resolveProduct(tag)) }}
                <span class="fc-row-pid">{{ productIdLabel(resolveProduct(tag)) }}</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
              <div v-else class="fc-row-title">
                {{ productTitle(resolveProduct(tag)) }}
                <span class="fc-row-pid">{{ productIdLabel(resolveProduct(tag)) }}</span>
              </div>
              <div class="fc-row-sub">
                Tagged by <strong>{{ userName(resolveTagger(tag)) }}</strong>
                <span v-if="userEmail(resolveTagger(tag))" class="fc-row-meta">
                  · {{ userEmail(resolveTagger(tag)) }}
                </span>
                <span class="fc-row-meta">· accepted {{ fmtDateTime(tag.taken_at || tag.created_at) }}</span>
              </div>
            </div>
            <span class="fc-status fc-status--ok">Accepted</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- ===== SECTION 4: Creator network — reuse list ===== -->
    <section v-if="!apiKeyBlocked" class="fc-section">
      <div class="fc-section-head">
        <span class="fc-eyebrow">Your creator network</span>
        <span v-if="counts.creatorNetwork" class="fc-count">
          {{ counts.creatorNetwork }}
        </span>
      </div>
      <h3 class="fc-section-title">Creators you can tag again</h3>
      <p class="fc-section-body">
        These creators previously approved your tagging request, so you can
        tag them again without a new approval.
      </p>

      <div class="fc-card">
        <div v-if="isLoading && !counts.creatorNetwork" class="fc-empty">
          Loading…
        </div>
        <template v-else-if="!counts.creatorNetwork">
          <div class="fc-empty-title">No accepted relationships yet</div>
          <div class="fc-empty-body">
            Once a tagged creator accepts your request, they'll appear here
            for quick reuse on future content.
          </div>
        </template>
        <ul v-else class="fc-list">
          <li v-for="u in creatorNetwork" :key="u.id" class="fc-row">
            <div class="fc-avatar">{{ userInitial(u) }}</div>
            <div class="fc-row-main">
              <div class="fc-row-title">{{ userName(u) }}</div>
              <div class="fc-row-sub">
                {{ u.productCount }}
                {{ u.productCount === 1 ? 'tagged product' : 'tagged products' }}
                <span class="fc-row-meta">· last accepted {{ fmtDate(u.lastAcceptedAt) }}</span>
              </div>
            </div>
            <span class="fc-status fc-status--ok">Reusable</span>
          </li>
        </ul>
      </div>
    </section>

  </div>
</template>

<style lang="scss" scoped>
.fc-root {
  display: flex; flex-direction: column;
  gap: 18px;
  padding: 8px 4px 32px;
}

// Header --------------------------------------------------------------------
.fc-head {
  position: relative;
  padding: 0 0 8px;
}
.fc-h1 {
  font-family: 'Inter', sans-serif;
  font-size: 22px; font-weight: 700;
  color: var(--ink-strong);
  margin: 0 0 4px;
  letter-spacing: 0.02em;
}
.fc-sub {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-muted);
  margin: 0;
  max-width: 720px;
}
.fc-refresh-btn {
  position: absolute; top: 0; right: 0;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1.5px solid var(--accent-ring);
  background: var(--bg-panel);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  transition: all 0.12s;
  &:hover:not(:disabled) {
    background: var(--accent-strong); color: #fff; border-color: var(--accent-strong);
  }
  &:disabled { opacity: 0.6; cursor: progress; }
}
@keyframes fc-rot { to { transform: rotate(360deg); } }
.fc-spin { animation: fc-rot 0.9s linear infinite; transform-origin: 50% 50%; }

// Warn / Error cards --------------------------------------------------------
.fc-warn-card {
  padding: 14px 18px;
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.10);
  border: 1px solid rgba(245, 158, 11, 0.30);
  color: #92400e;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; line-height: 1.55;
  a { color: #92400e; text-decoration: underline; font-weight: 700; }
}
.fc-error-card {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(185, 28, 28, 0.08);
  border: 1px solid rgba(185, 28, 28, 0.28);
  color: #991b1b;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
}
.fc-error-retry {
  padding: 5px 12px;
  border-radius: 7px;
  border: 1px solid #991b1b;
  background: transparent;
  color: #991b1b;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  &:hover { background: #991b1b; color: #fff; }
}

// Section -------------------------------------------------------------------
.fc-section {
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  padding: 18px 20px 20px;
  box-shadow: var(--shadow-soft);
}
.fc-section-head {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 6px;
}
.fc-eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--accent-strong);
  &--action { color: #b45309; }
}
.fc-count {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--accent-tint);
  color: var(--accent-strong);
  letter-spacing: 0.02em;
  &--action {
    background: rgba(245, 158, 11, 0.18);
    color: #b45309;
  }
}
.fc-section-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px; font-weight: 800;
  color: var(--ink-strong);
  margin: 0 0 4px;
}
.fc-section-body {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; color: var(--ink-muted);
  margin: 0 0 14px;
  line-height: 1.5;
}

// Inner card (white panel for the list / empty state)
.fc-card {
  background: var(--bg-main);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  padding: 18px 16px;
}

// Empty state inside a section card
.fc-empty {
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 12px; color: var(--ink-muted);
  padding: 22px 12px;
}
.fc-empty-title {
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 13.5px; font-weight: 700;
  color: var(--ink-strong);
  margin-bottom: 4px;
}
.fc-empty-body {
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; line-height: 1.5;
  color: var(--ink-muted);
  max-width: 480px;
  margin: 0 auto;
}

// Rows ---------------------------------------------------------------------
.fc-list {
  list-style: none;
  margin: 0; padding: 0;
  display: flex; flex-direction: column;
  gap: 8px;
}
.fc-row {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px;
  border-radius: 9px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  transition: border-color 0.12s, background 0.12s;
  &:hover {
    border-color: var(--accent-ring);
    background: rgba(13, 148, 136, 0.04);
  }
}
// Stacked variant for Incoming: title row + meta row + timestamp row.
// Top-aligned so the avatar lines up with the title, not the middle of
// the three-line block.
.fc-row--stacked {
  align-items: flex-start;
  padding: 12px;
  .fc-avatar { margin-top: 1px; }
  .fc-row-actions { align-self: center; }
}
.fc-row-title-line {
  display: flex; align-items: center; gap: 8px;
  flex-wrap: wrap;
}
.fc-row-sub--soft {
  color: var(--ink-muted);
  font-size: 11px;
  margin-top: 1px;
}
.fc-row-title--link {
  display: inline-flex; align-items: center; gap: 5px;
  color: var(--accent-strong);
  text-decoration: none;
  cursor: pointer;
  // Override the white-space:nowrap from the base .fc-row-title so longer
  // product titles can wrap when they sit on the stacked title-line row.
  white-space: normal;
  &:hover { text-decoration: underline; }
  svg { color: var(--accent); flex-shrink: 0; }
}
// "#5155" mini-tag — pinned next to the product title so the agent always
// has a stable identifier even if title enrichment is missing or the link
// click fails. Smaller + muted so it doesn't compete with the title.
.fc-row-pid {
  font-family: 'JetBrains Mono', 'Inter', monospace;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--ink-muted);
  background: var(--bg-subtle);
  padding: 1px 6px;
  border-radius: 4px;
  letter-spacing: 0.02em;
  // Don't underline the ID even when the parent link is hovered.
  text-decoration: none;
  .fc-row-title--link:hover & { text-decoration: none; }
}
.fc-avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-family: 'Libre Baskerville', serif;
  font-size: 14px; font-weight: 700;
  flex-shrink: 0;
  &--soft {
    background: var(--bg-subtle);
    color: var(--ink-medium);
  }
}
.fc-row-main {
  flex: 1; min-width: 0;
}
.fc-row-title {
  font-family: 'Libre Baskerville', serif;
  font-size: 13.5px; font-weight: 700;
  color: var(--ink-strong);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fc-row-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  strong { color: var(--ink-strong); font-weight: 600; }
}
.fc-row-meta {
  color: var(--ink-muted);
  font-weight: 400;
  font-size: 11px;
}
.fc-row-actions {
  display: inline-flex; gap: 6px;
  flex-shrink: 0;
}

// Buttons + Status pills ---------------------------------------------------
.fc-btn {
  padding: 7px 14px;
  border-radius: 7px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: all 0.12s;
  &:disabled { opacity: 0.55; cursor: progress; }
}
.fc-btn--primary {
  background: var(--accent-strong);
  color: #fff;
  border-color: var(--accent-strong);
  &:hover:not(:disabled) { background: var(--accent); border-color: var(--accent); }
}
.fc-btn--ghost {
  background: var(--bg-panel);
  color: var(--ink-medium);
  border-color: var(--border-medium);
  &:hover:not(:disabled) {
    background: var(--bg-subtle);
    color: var(--ink-strong);
    border-color: var(--ink-muted);
  }
}
.fc-status {
  display: inline-flex; align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  &--ok {
    background: var(--accent-tint);
    color: var(--accent-strong);
  }
  &--pending {
    background: rgba(245, 158, 11, 0.18);
    color: #b45309;
  }
}
</style>
